import React, { useEffect, useState, useCallback } from 'react';
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  RadioGroup,
  Radio,
  Skeleton,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { PhoneInput, FlagEmoji } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useNavigate, useParams } from 'react-router-dom';
import { BroadcastService } from '../../services/broadcast';
import useStore from '../../store/store';
import { LearnersService } from '../../services/learners';
import {
  formatDates,
  removeSpaces,
  setPlaceholderForInput,
} from '../../helpers';
import { ROLES } from '../../constants/auth';
import { diffuserDateTypes, diffuserTypes } from '../../constants/broadcast';
import {
  CalendarIcon,
  DatePicker,
  DateTimePicker,
  DesktopTimePicker,
} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { downloadImageBase64 } from '../../helpers/company';
import { CompanyServices } from '../../services/companies';
import { PhoneNumberUtil } from 'google-libphonenumber';
import ReturnButton from './components/ReturnButton';
import { isDisabledDate } from '../../helpers/dates';
import { ANEMON_HOLIDAYS } from '../../constants/dates';
import TypographyUI from '../../components/TypographyUI';
import i18n from "i18next";
import { useTranslation, initReactI18next } from "react-i18next";
import translations from '../../translations/translations.json';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';


i18n
  .use(initReactI18next)
  .init({
    resources: translations,
    lng: "fr",
    fallbackLng: "fr",
    interpolation: {
      escapeValue: false
    }
  });

function CompaignInvitation() {
  let { id } = useParams();
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isAccepted, setIsAccepted] = useState(false);
  const [isCGAConfirmer, setCGAConfirmer] = useState(false);
  const [broadcast, setBroadcast] = useState([]);
  const [isBroadcastloaded, setIsBroadcastloaded] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState('today');
  const [customDate, setCustomDate] = useState(() =>
    dayjs().add(10, 'minutes')
  );
  const navigate = useNavigate();
  const [isWeekendsMenu, setIsWeekendsMenu] = useState(false);
  const [menuValues, setMenuValues] = useState([]);
  const [menuDate, setMenuDate] = useState(() => dayjs().add(10, 'minutes'));
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [languageData, setLanguageData] = useState(null);
  const [organization, setOrganization] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [email, setEmail] = useState('');

  const handleLanguageChange = useCallback((event, fullLanguageData) => {
    setSelectedLanguage(event.target.value);
    // Keep only the fields we need for the API
    const apiLanguageData = {
      _id: fullLanguageData._id,
      name: fullLanguageData.name,
      code: fullLanguageData.code,
      active: fullLanguageData.active
    };
    setLanguageData(apiLanguageData);
    // Change the i18n language
    i18n.changeLanguage(event.target.value);
  }, []);

  const handleLanguageDataChange = useCallback((languagesData) => {
    if (selectedLanguage && languagesData[selectedLanguage]) {
      const langData = languagesData[selectedLanguage];
      const apiLanguageData = {
        _id: langData._id,
        name: langData.name,
        code: langData.code,
        active: langData.active
      };
      setLanguageData(apiLanguageData);
    }
  }, [selectedLanguage]);

  const { setSnackbarContent, companyLogo, setCompanyLogo } = useStore(
    (state) => state
  );

  const role = useStore((state) => state.userRole);
  const isAuhtorized = role !== ROLES.UNAUTHORIZED;
  const [calculatedTrainings, setCalculatedTrainings] = useState([]);
  const [isCalculatedTrainingsLoading, setIsCalculatedTrainingsLoading] =
    useState(false);

  const phoneUtil = PhoneNumberUtil.getInstance();

  const isPhoneValid = (phone) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const currentTime = dayjs();

  const isAllInputsFilled =
    ((broadcast.type === diffuserTypes.manual.value
      ? isAccepted &&
        isCGAConfirmer &&
        lastName &&
        firstName &&
        isPhoneValid(phoneNumber) &&
        menuDate?.isValid() &&
        currentTime.isBefore(menuDate) &&
        menuValues.length >= broadcast?.manual_broadcast?.min_trainings &&
        menuValues.length <= broadcast?.manual_broadcast?.max_trainings
      : isAccepted &&
        isCGAConfirmer &&
        lastName &&
        firstName &&
        isPhoneValid(phoneNumber) &&
        customDate?.isValid() &&
        currentTime.isBefore(customDate)
      ) && (!broadcast.isEmailMandatory || (broadcast.isEmailMandatory && !!email)));

  const getBroadcastById = async () => {
    const queryIdd = id;
    const isLink = queryIdd.length <= 6;

    try {
      const response = isLink
        ? await BroadcastService.getBroadcastByLink(id)
        : await BroadcastService.getBroadcastById(id);
      setIsBroadcastloaded(true);
      const broadcastData = response.data.data;
      setBroadcast(broadcastData);
      
      // Set language from broadcast if available
      if (broadcastData.language?.code) {
        setSelectedLanguage(broadcastData.language.code);
        i18n.changeLanguage(broadcastData.language.code);
      }

      if (broadcastData?.defaultTime) {
        const broadcastDefaultDate = dayjs()
          .add(broadcastData.defaultTime.day || 0, 'day')
          .add(broadcastData.defaultTime.hour || 0, 'hour')
          .add(broadcastData.defaultTime.minute || 10, 'minute')

        setCustomDate(broadcastDefaultDate);
        setMenuDate(broadcastDefaultDate);
      }
      
      return response.data.data;
    } catch (e) {
      console.error(e);
      setSnackbarContent('error', 'Error');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await getBroadcastById();

      if (!companyLogo && response && response?.company_id) {
        downloadImageBase64(
          CompanyServices.getCompanyLogo,
          response.company_id,
          setCompanyLogo
        );
      }
    };

    fetchData();
  }, []);

  const changeSelectedStartDate = (event) => {
    const value = event?.target?.value || event;
    setSelectedStartDate(value);

    const today = dayjs();

    switch (value) {
      case diffuserDateTypes.today.value:
        setCustomDate(today
          .add(broadcast?.defaultTime?.days || 0, 'day')
          .add(broadcast?.defaultTime?.hours || 0, 'hour')
          .add(broadcast?.defaultTime?.minutes || 10, 'minute'))
        break;
      case diffuserDateTypes.tomorrow.value:
        setCustomDate(today.add(1, 'day'));
        break;
      default:
        setCustomDate(today.add(2, 'day'));
    }
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handleAdditionalInfoChange = (event) => {
    setAdditionalInfo(event.target.value);
  };

  const handleCheckboxChange = (event, setter) => {
    setter(event.target.checked);
  };

  const handleEmail = (event) => {
    setEmail(event.target.value);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data =
        broadcast.type === diffuserTypes.manual.value
          ? {
              firstName: firstName,
              lastName: lastName,
              phoneNumber: removeSpaces(phoneNumber),
              start_date: menuDate,
              tags: [],
              company_id: broadcast?.company_id,
              broadcast_id: broadcast._id,
              include_weekends: isWeekendsMenu,
              selected_trainings: menuValues,
              language: broadcast.language,
              additional_info: additionalInfo,
              email: email,
            }
          : {
              firstName: firstName,
              lastName: lastName,
              phoneNumber: removeSpaces(phoneNumber),
              start_date: customDate,
              tags: [],
              company_id: broadcast?.company_id,
              broadcast_id: broadcast._id,
              language: broadcast.language,
              additional_info: additionalInfo,
              email: email,
            };

      const currentTime = dayjs();

      if (
        broadcast.type === diffuserTypes.timing.value &&
        (customDate.startOf('minute').isBefore(currentTime) ||
          customDate.startOf('minute').isSame(currentTime))
      ) {
        return setSnackbarContent(
          'error',
          'Veuillez sélectionner une date différente'
        );
      }

      if (
        broadcast.type === diffuserTypes.manual.value &&
        (menuDate.startOf('minute').isBefore(currentTime) ||
          menuDate.startOf('minute').isSame(currentTime))
      ) {
        return setSnackbarContent(
          'error',
          'Veuillez sélectionner une date différente'
        );
      }

      broadcast.type === diffuserTypes.manual.value
        ? await BroadcastService.manualRegistration(data)
        : await LearnersService.inviteLearner(data);
      setSnackbarContent('success', "L'apprenant a été invité avec succès");
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setAdditionalInfo('');
      setEmail('');
      navigate('/success');
    } catch (error) {
      console.error('Error creating learner:', error);
      setSnackbarContent('error', "Une erreur s'est produite lors de l'invitation de l'apprenant");
    }
  };
  const formatDate = (dateString) => {
    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    return new Date(dateString).toLocaleDateString(
      `${selectedLanguage}-${selectedLanguage.toUpperCase()}`, 
      options
    );
  };
  const labelStyle = {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.5,
    maxWidth: '700px',
    textAlign: 'left',
  };

  const returnBack = () => {
    navigate('/diffuser');
  };

  const changePhoneNumber = (phone) => {
    setPhoneNumber(phone);
  };

  useEffect(() => {
    setPlaceholderForInput({ timeout: 1000 });
  }, []);

  useEffect(() => {
    if (broadcast.type && broadcast.type !== diffuserTypes.manual.value) {
      const getCalculatedTrainingsWithTime = async () => {
        setIsCalculatedTrainingsLoading(true);
        try {
          const payload = {
            firstName: firstName,
            lastName: lastName,
            phoneNumber: removeSpaces(phoneNumber),
            start_date: customDate,
            tags: [],
            company_id: broadcast.company_id,
            broadcast_id: broadcast._id,
          };
          const response = await LearnersService.invitationPreview(payload);
          setCalculatedTrainings(response.data.data);
        } catch (e) {
          console.error(e);
        } finally {
          setIsCalculatedTrainingsLoading(false);
        }
      };

      getCalculatedTrainingsWithTime();
    }
  }, [customDate, broadcast.type]);

  if (!isBroadcastloaded) {
    return <div></div>;
  }

  const today = new Date();
  const startDate = new Date(broadcast?.trainings[0].date);

  const changeBroadcastTime = (newTime) => {
    const inputDate = new Date(newTime);

    const currentDate = new Date();

    const inputDateMidnight = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );

    const currentDateMidnight = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );

    const timeDiff = inputDateMidnight - currentDateMidnight;

    const daysDiff = timeDiff / (24 * 60 * 60 * 1000);

    if (daysDiff === 0) {
      changeSelectedStartDate(diffuserDateTypes.today.value);
    } else if (daysDiff === 1) {
      changeSelectedStartDate(diffuserDateTypes.tomorrow.value);
    } else {
      changeSelectedStartDate(diffuserDateTypes.custom.value);
    }

    setCustomDate(dayjs(newTime));
  };

  const changeMenuTrainings = (e, trainingId) => {
    if (trainingId) {
      const isAlreadySelected = menuValues.includes(trainingId);

      if (isAlreadySelected) {
        setMenuValues(menuValues.filter((str) => str !== trainingId));
      } else {
        if (menuValues.length === broadcast?.manual_broadcast?.max_trainings)
          return;

        setMenuValues([...menuValues, trainingId]);
      }
    } else {
      setMenuValues(e.target.value);
    }
  };

  const isManual = broadcast.type === diffuserTypes.manual.value;
  const isTiming = broadcast.type === diffuserTypes.timing.value;

  const isValidSpecific =
    startDate &&
    startDate > today &&
    broadcast.type === diffuserTypes.specific.value;

  const isNotValidSpecific =
    startDate &&
    startDate < today &&
    broadcast.type === diffuserTypes.specific.value;

  const isValidDate =
    (startDate &&
      startDate > today &&
      broadcast.type === diffuserTypes.specific.value) ||
    broadcast.type === diffuserTypes.timing.value;

  return (
    
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <ReturnButton returnBack={returnBack} isAuhtorized={isAuhtorized} />

      {/* Language Selection Dropdown */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: { xs: 'center', md: 'flex-end' },
        maxWidth: '770px', 
        width: '100%', 
        margin: '0 auto 20px auto',
        padding: { xs: '0 20px', md: 0 },
        position: 'relative',
        zIndex: 1000
      }}>
        <LanguageSelector
          selectedLanguage={selectedLanguage}
          onChange={handleLanguageChange}
          onLanguageDataChange={handleLanguageDataChange}
          label={t("Language")}
        />
      </Box>

      <Box>
        <TypographyUI color="#3c4043" align="center">
          {broadcast.name}
        </TypographyUI>

        {isValidSpecific ? (
          <Typography variant="body1" color="black">
            {t("Registration open until")} {formatDate(startDate)}
          </Typography>
        ) : null}

        {isNotValidSpecific ? (
          <Typography variant="body1">
            {t("Registration is no longer possible")}. {t("Have a question?")}{' '}
            {t("send us an email")}{' '}
            <a href="mailto:support@anemon.fr" target="_blank" rel="noreferrer">
              support@anemon.fr
            </a>
          </Typography>
        ) : null}

        {isTiming || isManual ? (
          <Typography variant="body1" color="black">
            {t("Registration open")}
          </Typography>
        ) : null}

        {isValidDate || isManual ? (
          <>
            {!isManual && (
              <>
                <Typography variant="body1" color="black">
                  {t("Registration Form")}
                </Typography>

                <Typography variant="body1" color="black">
                  {t("1 micro-training per day")}
                </Typography>

                <Typography variant="body1" color="black">
                  {t("Registration possible from")}{' '}
                  {broadcast.trainings && broadcast.trainings.length > 0
                    ? formatDate(customDate)
                    : 'N/A'}
                </Typography>

                <Typography variant="body1" color="black">
                  {t("Duration")}: {broadcast.trainings.length} {t("day(s)")}
                </Typography>
              </>
            )}

            <pre
              style={{
                maxWidth: '770px',
                margin: '15px auto 0 auto',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
              }}
            >
              {broadcast.description}
            </pre>
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div className="broadcast-invite-wrapper">
                <TextField
                  label={t("First Name")}
                  variant="outlined"
                  value={firstName}
                  onChange={handleFirstNameChange}
                  style={{
                    maxWidth: '770px',
                    width: '100%',
                    marginBottom: '20px',
                  }}
                />
                <TextField
                  label={t("Last Name")}
                  variant="outlined"
                  value={lastName}
                  onChange={handleLastNameChange}
                  style={{
                    maxWidth: '770px',
                    width: '100%',
                    marginBottom: '20px',
                  }}
                />
                <TextField
                  label={t("Organization")}
                  variant="outlined"
                  value={additionalInfo}
                  onChange={handleAdditionalInfoChange}
                  style={{
                    maxWidth: '770px',
                    width: '100%',
                    marginBottom: '20px',
                  }}
                />
                <Box
                  style={{
                    maxWidth: '770px',
                    width: '100%',
                    marginBottom: '20px',
                  }}
                >
                  <small
                    style={{
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      marginBottom: '5px',
                    }}
                  >
                    (ex: 33 6 85 53 99 59)
                  </small>
                  <PhoneInput
                    defaultCountry="fr"
                    labels={{ number: 'Numéro de téléphone' }}
                    value={phoneNumber}
                    disableCountryGuess={true}
                    forceDialCode={true}
                    onChange={(phone) => changePhoneNumber(phone)}
                    inputStyle={{ width: '100%', height: '50px' }}
                    countrySelectorStyleProps={{
                      buttonStyle: { height: '50px' },
                    }}
                  />
                </Box>
                <TextField
                  label={`${t("Email")}${broadcast.isEmailMandatory ? '*' : ''}`}
                  variant="outlined"
                  value={email}
                  onChange={handleEmail}
                  style={{
                    maxWidth: '770px',
                    width: '100%',
                    marginBottom: '20px',
                  }}
                />

                {broadcast.type === diffuserTypes.timing.value ? (
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="selected-date"
                      name="selected-date"
                      value={selectedStartDate}
                      onChange={changeSelectedStartDate}
                      className="broadcast-date-select"
                    >
                      {Object.values(diffuserDateTypes).map((date) => (
                        <FormControlLabel
                          value={date.value}
                          control={<Radio />}
                          label={date.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                ) : null}

                {broadcast.type === diffuserTypes.timing.value ? (
                  <>
                    <DatePicker
                      label={t("Date")}
                      value={customDate}
                      minDate={dayjs()}
                      onChange={changeBroadcastTime}
                      shouldDisableDate={(date) =>
                        isDisabledDate(date, ANEMON_HOLIDAYS)
                      }
                      renderInput={(params) => <TextField {...params} />}
                      sx={{ my: 2 }}
                    />
                    <DesktopTimePicker
                      label="Horaire de réception"
                      className="mobile-clickable"
                      value={customDate}
                      minTime={
                        dayjs().isSame(customDate, 'day')
                          ? dayjs().add(4, 'minutes')
                          : dayjs().startOf('day').add(4, 'minutes')
                      }
                      onChange={changeBroadcastTime}
                      timeSteps={{ hours: 1, minutes: 1 }}
                      renderInput={(params) => <TextField {...params} />}
                      sx={{ my: 2 }}
                    />
                  </>
                ) : null}

                {isCalculatedTrainingsLoading && (
                  <Skeleton
                    sx={{
                      height: 190,
                      width: '100%',
                      maxWidth: '770px',
                      marginBottom: '20px',
                    }}
                    animation="wave"
                    variant="rectangular"
                  />
                )}

                {isManual && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '20px',
                      marginBottom: '15px',
                    }}
                  >
                    <Box>
                      <Typography>
                        Je sélectionne entre{' '}
                        {broadcast?.manual_broadcast?.min_trainings} et{' '}
                        {broadcast?.manual_broadcast?.max_trainings} contenus
                      </Typography>
                    </Box>
                  </Box>
                )}

                {isManual && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '20px',
                      flexDirection: 'column',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow className="green-border">
                          <TableCell></TableCell>
                          <TableCell align="center">{t("Micro-training")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {broadcast.trainings.map((training, index) => (
                          <TableRow key={index} className="green-border">
                            <TableCell align="center" className="green-border">
                              <Checkbox
                                checked={menuValues.includes(training.id)}
                                onChange={(e) => {
                                  changeMenuTrainings(e, training.id);
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" className="green-border">
                              {training.name}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography textAlign="left">
                        {t("I want to receive content during weekends")}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isWeekendsMenu}
                            onChange={(e) => {
                              setIsWeekendsMenu(true);
                            }}
                            style={{ marginRight: '10px', top: '0' }}
                          />
                        }
                        label={t("Yes")}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!isWeekendsMenu}
                            onChange={(e) => {
                              setIsWeekendsMenu(false);
                            }}
                            style={{ marginRight: '10px', top: '0' }}
                          />
                        }
                        label={t("No")}
                      />
                    </Box>

                    <Typography textAlign="left">
                      {t("I choose the start date and reception time")}
                      <br />
                      {t("I will receive content every day")}
                    </Typography>

                    <DateTimePicker
                      ampm={false}
                      label={t("Date")}
                      timeSteps={{ hours: 1, minutes: 1 }}
                      value={menuDate}
                      onChange={(date) => setMenuDate(date)}
                      disablePast={true}
                      minDateTime={dayjs().add(4, 'minutes')}
                      components={{
                        OpenPickerIcon: ({ ...other }) => (
                          <CalendarIcon
                            style={{ color: '#66DEAE' }}
                            {...other}
                          />
                        ),
                      }}
                    />
                  </Box>
                )}

                {calculatedTrainings.length && !isCalculatedTrainingsLoading ? (
                  <TableContainer
                    style={{
                      margin: '20px 0',
                      maxWidth: '770px',
                      overflowX: 'initial',
                    }}
                  >
                    <Table className="compaign-invite__table">
                      <TableHead>
                        <TableRow className="green-border">
                          <TableCell>{t("Title")}</TableCell>
                          <TableCell>{t("Day of reception")}</TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {t("Date of reception")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {calculatedTrainings.map((training, key) => {
                          // Update locale based on selected language
                          const daysOptions = { weekday: 'long' };
                          const localizedDate = new Intl.DateTimeFormat(
                            `${selectedLanguage}-${selectedLanguage.toUpperCase()}`,
                            daysOptions
                          ).format(new Date(training.date));

                          // Time in the format HH:mm
                          const timeOptions = {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          };
                          const formattedTime = new Date(
                            training.date
                          ).toLocaleTimeString('en-US', timeOptions);

                          const capitalizedDate =
                            localizedDate.charAt(0).toUpperCase() +
                            localizedDate.slice(1);

                          return (
                            <TableRow key={key} className="green-border">
                              <TableCell className="green-border">
                                {training.name}
                              </TableCell>
                              <TableCell className="green-border">
                                {capitalizedDate}
                              </TableCell>
                              <TableCell
                                className="green-border"
                                sx={{ textAlign: 'center' }}
                              >
                                <Box>
                                  {training.date &&
                                    formatDates(new Date(training.date))}
                                </Box>
                                <Box>{formattedTime}</Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : null}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAccepted}
                      onChange={(event) => handleCheckboxChange(event, setIsAccepted)}
                      style={{ marginRight: '10px', top: '0' }}
                    />
                  }
                  label={<div style={labelStyle}>{broadcast.conditions}</div>}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCGAConfirmer}
                      onChange={(event) => handleCheckboxChange(event, setCGAConfirmer)}
                      style={{ marginRight: '10px', top: '0' }}
                    />
                  }
                  label={
                    <div style={labelStyle}>
                      En validant mon inscription, je confirme avoir lu et accepté les 
                      <a style={{ color: "#4546CD" }} href="https://drive.google.com/file/d/1te179MM6Xs2H9zXMxhjV9MO1wThTpRzr/view" target="_blank"> Conditions Générales d’Utilisation</a>.
                    </div>
                  }
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '20px',
                  width: '100%',
                }}
              >
                <Button
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  style={{
                    marginRight: '10px',
                    borderRadius: '20px',
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    border: '1px solid rgba(211, 47, 47, 0.5)',
                    color: 'rgba(211, 47, 47, 0.5)',
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!isAllInputsFilled}
                  style={{
                    marginRight: '10px',
                    borderRadius: '20px',
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    backgroundColor: isAllInputsFilled ? '#66DEAE' : undefined,
                    color: isAllInputsFilled ? 'black' : undefined,
                  }}
                >
                  {t("I want to participate")}
                </Button>
              </div>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

export default CompaignInvitation;
